// PASSWORD NEVER TRANSMITTED - client-side only
// SHA-1 hashing and HIBP k-anonymity check run entirely in the browser.
// No password data is ever sent to Shaheen servers.

import { useState, useMemo } from 'react';
import zxcvbn from 'zxcvbn';

async function sha1Hex(str) {
  const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

async function checkPwned(password) {
  const hash = await sha1Hex(password);
  const prefix = hash.slice(0, 5);
  const suffix = hash.slice(5);
  // Direct browser → api.pwnedpasswords.com — never proxied through Shaheen backend
  const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
    headers: { 'Add-Padding': 'true' },
  });
  if (!res.ok) throw new Error(`HIBP ${res.status}`);
  const text = await res.text();
  for (const line of text.split('\n')) {
    const [h, c] = line.split(':');
    if (h.trim() === suffix) return { found: true, count: parseInt(c.trim(), 10) };
  }
  return { found: false, count: 0 };
}

const SCORE_LABELS = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
const SCORE_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

const CRACK_SCENARIOS = [
  { key: 'online_throttling_100_per_hour',       label: 'Someone guessing on a website', hint: 'Attacker trying passwords on a login page (rate limited)' },
  { key: 'offline_slow_hashing_1e4_per_second',  label: 'Stolen database (standard)',    hint: 'Attacker cracking a stolen password database using basic hardware' },
  { key: 'offline_fast_hashing_1e10_per_second', label: 'Stolen database (advanced)',    hint: 'Professional attacker using specialist hardware. Assume this is possible for any leaked database. Use unique passwords for every service.' },
];

const HOW_STEPS = [
  { n: '1', title: 'Stays on your device', desc: 'Your password never leaves your device. (It is converted to a hashed fingerprint locally before anything happens.)' },
  { n: '2', title: 'Only a fragment is sent', desc: 'A small anonymous fragment is sent to the breach database, never the password itself. (First 5 characters of the fingerprint only.)' },
  { n: '3', title: 'Thousands returned', desc: 'The database sends back thousands of similar-looking results, making it impossible to identify which one you checked.' },
  { n: '4', title: 'Matched locally', desc: 'Your device does the final comparison privately. The breach database never learns what you searched for.' },
];

export default function PasswordTools() {
  const [password, setPassword]       = useState('');
  const [showPw, setShowPw]           = useState(false);
  const [howOpen, setHowOpen]         = useState(false);
  const [checking, setChecking]       = useState(false);
  const [breachResult, setBreachResult] = useState(null);
  const [breachError, setBreachError] = useState('');

  const strength = useMemo(() => (password ? zxcvbn(password) : null), [password]);

  const handleCheck = async () => {
    if (!password) return;
    setChecking(true);
    setBreachResult(null);
    setBreachError('');
    try {
      setBreachResult(await checkPwned(password));
    } catch {
      setBreachError('Could not reach the breach database. Check your internet connection and try again.');
    } finally {
      setChecking(false);
    }
  };

  const scoreColor = strength ? SCORE_COLORS[strength.score] : '#555';
  const scoreLabel = strength ? SCORE_LABELS[strength.score] : '';

  return (
    <div style={{ padding: '20px 16px', overflowY: 'auto', flex: 1 }}>
      <div style={{ maxWidth: 580 }}>
        <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
          Password Tools
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
          Check if your password has been exposed and analyse its strength
        </div>

        {/* Privacy notice */}
        <div style={{
          background: 'rgba(212,175,55,0.07)', border: '0.5px solid rgba(212,175,55,0.35)',
          borderRadius: 10, padding: '14px 16px', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gold)', marginBottom: 5 }}>
                Your password never leaves your device
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                Not sent to Shaheen servers, not visible to staff, never stored or logged.
                We use the same privacy-preserving technique trusted by Google and Apple.
              </div>
            </div>
          </div>
        </div>

        {/* How does this work? */}
        <div style={{ marginBottom: 18 }}>
          <button
            onClick={() => setHowOpen(p => !p)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
              display: 'flex', alignItems: 'center', gap: 6,
              color: 'var(--text-muted)', fontSize: 12,
            }}
          >
            <svg
              width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ transform: howOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            How does this work?
          </button>

          {howOpen && (
            <div style={{
              background: 'var(--bg-raised)', border: '0.5px solid var(--border)',
              borderRadius: 10, padding: '18px 16px', marginTop: 8,
            }}>
              {/* Step flow diagram */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 16 }}>
                {HOW_STEPS.map((step, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: 'var(--gold)', color: 'var(--bg-base)',
                        fontSize: 11, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 8px',
                      }}>
                        {step.n}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                        {step.title}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        {step.desc}
                      </div>
                    </div>
                    {i < 3 && (
                      <div style={{
                        position: 'absolute', top: 12, right: -8, zIndex: 1,
                        color: 'var(--text-muted)', fontSize: 14, lineHeight: 1,
                      }}>›</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ height: '0.5px', background: 'var(--border)', margin: '4px 0 12px' }} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                Even if the breach database servers were compromised, they would only ever see a small fragment
                of a fingerprint: not your password, not the full fingerprint, and nothing that could identify you.
              </div>
            </div>
          )}
        </div>

        {/* Password input */}
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <input
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setBreachResult(null);
              setBreachError('');
            }}
            placeholder="Enter a password to analyse"
            autoComplete="new-password"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg-raised)', border: '0.5px solid var(--border)',
              borderRadius: 8, padding: '12px 46px 12px 14px',
              color: 'var(--text-primary)', fontSize: 14, outline: 'none',
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--gold)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
          />
          <button
            onClick={() => setShowPw(p => !p)}
            tabIndex={-1}
            aria-label={showPw ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
            }}
          >
            {showPw ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>

        {/* Strength meter — live as user types */}
        {strength && (
          <div style={{
            background: 'var(--bg-surface)', border: '0.5px solid var(--border)',
            borderRadius: 10, padding: '16px', marginBottom: 14,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>STRENGTH</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: scoreColor }}>{scoreLabel}</div>
            </div>

            {/* Bar */}
            <div style={{ height: 5, background: 'var(--bg-raised)', borderRadius: 3, marginBottom: 16, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: `${(strength.score + 1) * 20}%`,
                background: scoreColor,
                transition: 'width 0.25s ease, background 0.25s ease',
              }} />
            </div>

            {/* Crack times */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.08em', marginBottom: 8 }}>
                TIME TO CRACK
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {CRACK_SCENARIOS.map(({ key, label, hint }) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {label}{' '}
                      <span style={{ fontSize: 10, opacity: 0.55 }}>({hint})</span>
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                      {strength.crack_times_display[key]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Crack time note */}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7, background: 'var(--bg-raised)', borderRadius: 7, padding: '10px 12px', marginBottom: 14 }}>
              Even a strong password can be exposed if the website storing it is hacked.
              The best protection is using a different password for every account. Your concierge can recommend a password manager.
            </div>

            {/* Feedback */}
            {(strength.feedback.warning || strength.feedback.suggestions?.length > 0) && (
              <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 12 }}>
                {strength.feedback.warning && (
                  <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 8, display: 'flex', gap: 6 }}>
                    <span>⚠</span>
                    <span>{strength.feedback.warning}</span>
                  </div>
                )}
                {strength.feedback.suggestions?.map((suggestion, i) => (
                  <div key={i} style={{
                    fontSize: 12, color: 'var(--text-muted)', marginBottom: 5,
                    paddingLeft: 14, position: 'relative', lineHeight: 1.5,
                  }}>
                    <span style={{ position: 'absolute', left: 3, color: 'var(--gold)' }}>·</span>
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Breach check button */}
        <button
          onClick={handleCheck}
          disabled={!password || checking}
          style={{
            width: '100%', padding: '12px', borderRadius: 8, border: 'none',
            background: password && !checking ? 'var(--gold)' : 'var(--bg-raised)',
            color: password && !checking ? 'var(--bg-base)' : 'var(--text-muted)',
            fontSize: 13, fontWeight: 500,
            cursor: password && !checking ? 'pointer' : 'default',
            transition: 'all 0.15s', marginBottom: 14,
          }}
        >
          {checking ? 'Checking breach databases…' : 'Check if this password has been leaked'}
        </button>

        {/* Breach result */}
        {breachResult && !breachError && (
          <div style={{
            borderRadius: 10, padding: '14px 16px', marginBottom: 14,
            background: breachResult.found ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
            border: `0.5px solid ${breachResult.found ? 'var(--red)' : 'var(--green)'}`,
          }}>
            {breachResult.found ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>
                  Found in {breachResult.count.toLocaleString()} known data{' '}
                  {breachResult.count === 1 ? 'breach' : 'breaches'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  This password has been exposed in public data breaches. Do not use it for any account.
                  Change it immediately everywhere it is currently in use.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)', marginBottom: 6 }}>
                  Not found in any known breach
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  This password does not appear in known breach databases. A clean result here does not
                  guarantee it is strong. Check the strength analysis above.
                </div>
              </>
            )}
          </div>
        )}

        {breachError && (
          <div style={{
            borderRadius: 10, padding: '12px 14px', marginBottom: 14,
            background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)',
            fontSize: 12, color: 'var(--red)', lineHeight: 1.6,
          }}>
            {breachError}
          </div>
        )}
      </div>
    </div>
  );
}
